import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/icons";

const DOWNLOAD_BASE = "/releases";

interface ReleaseInfo {
  version: string;
  mac?: { url: string; filename: string; size?: string };
  windows?: { url: string; filename: string; size?: string };
}

export function Download() {
  const [release, setRelease] = useState<ReleaseInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${DOWNLOAD_BASE}/latest.json`)
      .then((r) => r.json())
      .then((data) => setRelease(data))
      .catch(() => setRelease(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="auth-shell" style={{ alignItems: "flex-start", paddingTop: 60, paddingBottom: 60 }}>
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 560 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div className="ph-eyebrow" style={{ justifyContent: "center", display: "inline-flex" }}>
            Desktop
          </div>
          <h1 className="ph-title" style={{ fontSize: 32, marginTop: 8 }}>Chatter Desktop</h1>
          <p className="ph-lede" style={{ marginInline: "auto" }}>
            Get the desktop app for YouTube voice import and more — downloads audio
            directly on your machine, no restrictions.
          </p>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 40, display: "grid", placeItems: "center", gap: 12 }}>
            <div className="spin" />
            <p style={{ color: "var(--ink-3)", fontSize: 12.5 }}>Checking for downloads…</p>
          </div>
        ) : release ? (
          <div className="stack-v">
            <div style={{ textAlign: "center" }}>
              <span className="pill">v{release.version}</span>
            </div>

            {release.mac && (
              <a href={release.mac.url} className="qa-card" style={{ textDecoration: "none" }}>
                <span className="qa-icon"><Icon.Download /></span>
                <span className="qa-body">
                  <span className="qa-t">macOS (Apple Silicon &amp; Intel)</span>
                  <span className="qa-d">
                    {release.mac.filename}
                    {release.mac.size ? ` · ${release.mac.size}` : ""}
                  </span>
                </span>
                <span className="qa-arrow"><Icon.Arrow /></span>
              </a>
            )}

            {release.windows && (
              <a href={release.windows.url} className="qa-card" style={{ textDecoration: "none" }}>
                <span className="qa-icon"><Icon.Download /></span>
                <span className="qa-body">
                  <span className="qa-t">Windows (64-bit)</span>
                  <span className="qa-d">
                    {release.windows.filename}
                    {release.windows.size ? ` · ${release.windows.size}` : ""}
                  </span>
                </span>
                <span className="qa-arrow"><Icon.Arrow /></span>
              </a>
            )}

            {release.mac && (
              <div className="card" style={{ padding: 16 }}>
                <div className="ph-eyebrow" style={{ marginBottom: 6 }}>macOS Gatekeeper</div>
                <p style={{ fontSize: 12.5, color: "var(--ink-3)", margin: "0 0 10px", lineHeight: 1.5 }}>
                  The app isn't code-signed yet, so macOS may block it. After installing, run:
                </p>
                <code
                  style={{
                    display: "block",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12,
                    color: "var(--amber)",
                    background: "oklch(0.16 0.012 265)",
                    border: "1px solid var(--hair)",
                    padding: "8px 10px",
                    borderRadius: 6,
                    userSelect: "all",
                  }}
                >
                  xattr -c /Applications/Chatter.app
                </code>
              </div>
            )}

            <div className="card" style={{ padding: 16 }}>
              <div className="ph-eyebrow" style={{ marginBottom: 8 }}>Requirements</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { tool: "yt-dlp", url: "https://github.com/yt-dlp/yt-dlp#installation", note: "for YouTube imports" },
                  { tool: "ffmpeg", url: "https://ffmpeg.org/download.html", note: "for audio conversion" },
                  { tool: "deno", url: "https://deno.land", note: "(required by yt-dlp for YouTube downloads)" },
                ].map(({ tool, url, note }) => (
                  <li
                    key={tool}
                    style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--ink-3)" }}
                  >
                    <span style={{ color: "var(--green)" }}>✓</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--accent)", textDecoration: "none" }}
                    >
                      {tool}
                    </a>
                    <span>installed {note}</span>
                  </li>
                ))}
              </ul>
              <p style={{ fontSize: 11.5, color: "var(--ink-4)", margin: "12px 0 0", fontFamily: "JetBrains Mono, monospace" }}>
                macOS: brew install yt-dlp ffmpeg deno
              </p>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="ico"><Icon.Download /></div>
            <h3>No releases yet</h3>
            <p>Check back soon!</p>
          </div>
        )}

        <div style={{ marginTop: 32, textAlign: "center" }}>
          <Link to="/login" className="auth-link">
            <Icon.ArrowL /> Back to web app
          </Link>
        </div>
      </div>
    </div>
  );
}
